import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Btn, dt } from "../components/common";
import { toast } from "sonner";
import { Mail, Phone, MessageSquare, Inbox, Trash2, Sparkles } from "lucide-react";
import { LigarButton } from "../components/WebphoneDialog";
import ActivityModal from "./crm/ActivityModal";

const CANAL_META = {
  email: { label: "Email", icon: Mail },
  chamada: { label: "Telefone", icon: Phone },
  chat: { label: "Chat do site", icon: MessageSquare },
};

export default function Channels() {
  const [stats, setStats] = useState({ email: 0, chamada: 0, chat: 0, total: 0 });
  const [leads, setLeads] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [busy, setBusy] = useState({ cae: false, orphans: false });

  // Activity modal state
  const [showActivity, setShowActivity] = useState(false);
  const [actCtx, setActCtx] = useState({ accountId: "", accountNome: "" });

  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/channels/stats"),
        api.get(`/channels/leads${filtro ? `?canal=${filtro}` : ""}`),
      ]);
      setStats(s.data); setLeads(l.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [filtro]); // eslint-disable-line

  const deleteLead = async (id) => {
    if (!window.confirm("Eliminar este evento de canal?")) return;
    try {
      await api.delete(`/channels/leads/${id}`);
      toast.success("Entrada eliminada.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao eliminar."); }
  };

  const cleanupOrphans = async () => {
    setBusy((b) => ({ ...b, orphans: true }));
    try {
      const { data } = await api.post("/channels/cleanup-orphans");
      toast.success(data.removed > 0 ? `${data.removed} entradas orfas removidas.` : "Sem entradas orfas para remover.");
      if (data.removed > 0) load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao limpar orfaos."); }
    finally { setBusy((b) => ({ ...b, orphans: false })); }
  };

  const fillCaeBulk = async () => {
    setBusy((b) => ({ ...b, cae: true }));
    try {
      const { data } = await api.post("/crm/accounts/fill-cae-bulk");
      toast.success(data.atualizadas > 0
        ? `CAE preenchido em ${data.atualizadas} conta(s). Restam ${data.total_sem_cae - data.atualizadas} sem CAE.`
        : "Sem contas a processar ou nenhum CAE determinado.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao preencher CAE."); }
    finally { setBusy((b) => ({ ...b, cae: false })); }
  };

  const cards = [
    { key: "email", ...CANAL_META.email, val: stats.email, hint: `${backend}/api/webhooks/inbound-email` },
    { key: "chamada", ...CANAL_META.chamada, val: stats.chamada, hint: `${backend}/api/webhooks/call` },
    { key: "chat", ...CANAL_META.chat, val: stats.chat, hint: `${backend}/api/public/chat` },
  ];

  const cols = [
    { key: "canal", label: "Canal", render: (r) => {
      const M = CANAL_META[r.canal] || {}; const I = M.icon || Inbox;
      return <span className="inline-flex items-center gap-1.5 font-semibold text-navy"><I size={14} /> {M.label || r.canal}</span>;
    }},
    { key: "organizacao", label: "Contacto / Organizacao", render: (r) => <span>{r.organizacao || "-"}</span> },
    { key: "resumo", label: "Resumo", render: (r) => <span className="text-xs text-muted2">{JSON.stringify(r.resumo || {}).slice(0, 80)}</span> },
    { key: "em", label: "Quando", render: (r) => dt(r.em) },
    { key: "acoes", label: "", render: (r) => (
      <div className="flex items-center gap-2">
        {r.resumo?.telefone && <LigarButton number={r.resumo.telefone} label="Ligar" />}
        {r.account_id && (
          <>
            <a href={`/app/crm/contas/${r.account_id}`} className="text-blue text-xs hover:underline" data-testid={`channel-lead-${r.account_id}`}>Abrir conta</a>
            <button onClick={() => { setActCtx({ accountId: r.account_id, accountNome: r.organizacao || "Conta" }); setShowActivity(true); }}
              className="text-xs text-slate-500 hover:text-navy hover:underline" data-testid={`channel-activity-${r.account_id}`}>
              Registar atividade
            </button>
          </>
        )}
        {r.id && (
          <button onClick={() => deleteLead(r.id)} data-testid={`channel-delete-${r.id}`}
            className="text-red-500 hover:text-red-700 p-0.5 rounded transition-colors">
            <Trash2 size={13} />
          </button>
        )}
      </div>
    )},
  ];

  return (
    <div>
      <PageHeader overline="Captacao multicanal (M12)" title="Canais"
        action={
          <div className="flex gap-2 flex-wrap">
            <Btn variant="ghost" onClick={cleanupOrphans} disabled={busy.orphans} testid="cleanup-orphans-btn">
              {busy.orphans ? "A limpar..." : "Limpar orfaos"}
            </Btn>
            <Btn variant="ghost" onClick={fillCaeBulk} disabled={busy.cae} testid="fill-cae-bulk-btn">
              <Sparkles size={13} /> {busy.cae ? "A processar..." : "Preencher CAE em falta"}
            </Btn>
            <Btn onClick={() => { setActCtx({ accountId: "", accountNome: "" }); setShowActivity(true); }} testid="new-activity-btn">
              Registar atividade
            </Btn>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {cards.map((c) => (
          <button key={c.key} onClick={() => setFiltro(filtro === c.key ? "" : c.key)} data-testid={`channel-card-${c.key}`}
            className={`text-left border rounded-xl p-4 transition-colors ${filtro === c.key ? "border-blue bg-tint" : "border-tint hover:border-blue"}`}>
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 font-semibold text-navy"><c.icon size={18} className="text-blue" /> {c.label}</span>
              <span className="text-2xl font-bold text-navy">{c.val}</span>
            </div>
            <div className="text-[10px] text-muted2 mt-2 break-all">{c.hint}</div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm text-muted2">Leads recebidos</span>
        {filtro && <button onClick={() => setFiltro("")} className="text-xs px-2 py-0.5 rounded-full bg-tint text-navy" data-testid="channel-clear-filter">{CANAL_META[filtro]?.label} x</button>}
      </div>
      <Table columns={cols} rows={leads} testid="channels-leads-table" />

      <p className="text-xs text-muted2 mt-6">A configuracao dos canais (SMS Buzz, Let's Call e widget de chat) esta em Definicoes - Canais.</p>

      <ActivityModal
        open={showActivity}
        onClose={() => setShowActivity(false)}
        accountId={actCtx.accountId}
        accountNome={actCtx.accountNome}
        contacts={[]}
        onSuccess={load}
      />
    </div>
  );
}
